/* eslint-disable react/no-unescaped-entities */
/**
 * Ask AI Journey Modal
 *
 * Michael's Feb 2026 "Create My Journey" flow:
 *   User types a natural-language intent → Claude interprets and
 *   selects from Reclaim's canonical affirmation library (80/20
 *   canonical/personalised split) → user reviews → journey is
 *   composed via the existing audio-journeys pipeline.
 *
 * Philosophy: SEARCH → REUSE → ADAPT ONLY IF NECESSARY → CREATE
 * LAST. The AI is our librarian, not our writer.
 */
import { queueLabel } from "../lib/queueLabel";
import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2, ChevronRight, ChevronLeft, BookOpen, Star, Check, Mic, Square, Play, Pencil, Trash2, Plus, Lock } from "lucide-react";
import ReadAloud from "../components/ReadAloud";
import InstallOrb from "../components/InstallOrb";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// Reclaim voice presets (Feb 24 2026 · Michael's ask).
// A user who chooses "Create with the Reclaim voice" can now pick
// female (Lily — soft British) or male (Michael's cloned voice). If
// the user picks "female" we pass no `narrator_voice_id` — the
// backend default IS Lily. Male sends Michael's cloned voice id.
const RECLAIM_VOICE_MALE_ID = "3BrRQZxtiIYX5Ytyvl1h"; // Michael v3
const RECLAIM_VOICE_LILY_ID = "pFZP5JQG7iQjIQuC4Bku"; // backend default

// Humanize Claude's structured "why" reasons so the personalised
// note reads like a warm explanation, not a code comment. Strips
// developer verbs (user requested, canonical lines don't affirm,
// etc.) and preserves the poetic essence of what changed.
function humanizeWhy(raw) {
  if (!raw || typeof raw !== "string") return "";
  let s = raw.trim();
  // Kill machine-y phrases that don't belong in front of the user.
  s = s
    .replace(/user (specifically )?(asked for|requested|wanted)\s*/gi, "")
    .replace(/canonical (lines?|library|framework)\s*/gi, "the library ")
    .replace(/does(?:n't| not)\s+(directly\s+)?(affirm|address|include|cover)\s*/gi, "doesn't quite carry ")
    .replace(/not (directly\s+)?(present|covered) in.*$/gi, "isn't in the library yet")
    .replace(/\s+but\s+.*canonical.*$/gi, "")
    .replace(/;.*$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "it fit what you asked for";
  // Ensure lowercase first letter so it flows into "Soulumo added this because …"
  return s.charAt(0).toLowerCase() + s.slice(1).replace(/\.$/, "") + ".";
}


const TOK = {
  // Soulumo palette — Collective Alignment inspired (Feb 2026).
  ink: "#0d1f3a",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.45)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  goldGlow: "rgba(231,201,138,0.16)",
  personal: "#f5e0ad",
  // Modal panel gradient — matches the deep-navy body.
  panelBg: "linear-gradient(180deg, #1a2c4a 0%, #0d1f3a 100%)",
};

const SECTION_LABELS = {
  truth: "Truth",
  nothing_missing: "Nothing Missing",
  trust: "Trust",
  identity: "Identity",
  performance: "Performance",
  final_loop: "Final Loop",
};

const EXAMPLE_INTENTS = [
  "I've got a golf competition on Saturday. I'm playing really well, but when I start scoring well I become protective and tighten up.",
  "I've returned to work after selling my company and I keep feeling that everyone assumes I should know everything.",
  "I want to speak on stage without my voice shaking or losing my thread.",
];

export default function AskAiJourneyModal({ onClose, onCreated, onBack, initialIntent = "" }) {
  const [step, setStep] = useState("intent"); // intent | thinking | review | preRecord | creating | landed | record
  const [preCreateLines, setPreCreateLines] = useState([]);
  const [intent, setIntent] = useState(initialIntent);
  const [duration, setDuration] = useState(12);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  // Series build-up (Michael's series suggestion, Feb 2026):
  // one tap → 3 journeys. Foundation · Trust · Main. All from the
  // same canonical picks — zero extra LLM cost.
  const [addBuildUp, setAddBuildUp] = useState(false);
  // Intention Primer — pure tone prepended to the finished journey.
  // Defaults to `null` (no primer). The user picks it explicitly on
  // the Review screen — no auto-prepend without their consent.
  const [primerHz, setPrimerHz] = useState(null);
  // Voice preference chosen BEFORE final composition (Feb 2026
  // Soulumo brief). "mine" = prefer user's recorded voice where
  // available, otherwise Reclaim voice. "reclaim" = full Reclaim
  // voice throughout. Default is "mine" since it's the recommended
  // and cheapest path.
  const [voiceChoice, setVoiceChoice] = useState("mine");
  // When voiceChoice === "reclaim", the user can also pick the
  // gender of the narrator. Default female (Lily) matches the
  // backend default and the copy Michael has always shown users;
  // male routes through his own cloned voice id.
  const [reclaimVoiceGender, setReclaimVoiceGender] = useState("female");
  // Interpretation gate: after Claude replies with a plan, we
  // FIRST show only the interpretation (Situation / Pattern /
  // Desired state / Core shift). The user confirms ("Yes, that's
  // it") or refines it before the full plan is revealed.
  const [interpConfirmed, setInterpConfirmed] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  // Voice-bank match map: {normalized_text: {recorded, audio_url}}.
  // Populated after a plan lands so the review screen can badge
  // each line as "In My Voice" or surface a Record control.
  const [voiceMatch, setVoiceMatch] = useState({});
  // Journey created — used to jump back into the record step after
  // creation if there were personalised lines.
  const [createdJourney, setCreatedJourney] = useState(null);

  // iOS-safe body scroll lock — freezes the page beneath so Safari
  // doesn't rubber-band while you type the intent. Restores the
  // scroll position exactly on unmount.
  React.useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overscroll: body.style.overscrollBehavior,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overscrollBehavior = prev.overscroll;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const canSubmit = intent.trim().length >= 8;

  // Every personalised line across the plan — used by the voice bank
  // step to give the user a 30-second per-line recorder.
  const personalisedLines = React.useMemo(() => {
    if (!plan) return [];
    const acc = [];
    for (const sec of plan.sections || []) {
      for (const ln of sec.lines || []) {
        if (ln.source === "personalised") acc.push({ ...ln, section: sec.section });
      }
    }
    return acc;
  }, [plan]);

  const handleAsk = async () => {
    if (!canSubmit) return;
    setStep("thinking");
    setError(null);
    setInterpConfirmed(false);
    setRefineText("");
    // Small retry so a transient 5xx / 404 during a hot-reload
    // deploy is invisible to the user. Each attempt gives Claude
    // enough time to respond (endpoint takes 10-30s).
    const attempt = async (n) => {
      try {
        const r = await axios.post(
          `${API}/api/audio-journeys/ai/ask`,
          { intent_text: intent.trim(), duration_minutes: duration },
          auth(),
        );
        setPlan(r.data);
        setStep("review");
        // Fetch which lines are already in the user's voice bank so
        // the review screen shows honest "In My Voice" badges +
        // records only genuinely new lines.
        matchVoiceBank(r.data);
      } catch (e) {
        const status = e?.response?.status;
        const retriable = !status || status === 404 || status === 502 || status === 503 || status === 504;
        if (retriable && n < 2) {
          await new Promise((res) => setTimeout(res, 1200 + n * 800));
          return attempt(n + 1);
        }
        const raw = e?.response?.data?.detail || e?.message || "Soulumo Coach needs another moment — please try again.";
        setError(typeof raw === "string" ? raw : (raw?.message || "Something went wrong."));
        setStep("intent");
      }
    };
    attempt(0);
  };

  // Refine — user tells Soulumo "that's not quite what I mean" and
  // adds a clarification. We append to the original intent and
  // re-ask, replacing the plan.
  const handleRefine = async () => {
    const clarification = (refineText || "").trim();
    if (!clarification) return;
    setRefining(true);
    setError(null);
    const nextIntent = `${intent.trim()}\n\nOne more note: ${clarification}`;
    try {
      const r = await axios.post(
        `${API}/api/audio-journeys/ai/ask`,
        { intent_text: nextIntent, duration_minutes: duration },
        auth(),
      );
      setPlan(r.data);
      setIntent(nextIntent);
      setRefineText("");
      // Collapse the refine textarea so the fresh interpretation reads clean.
      setInterpConfirmed(false);
      matchVoiceBank(r.data);
    } catch (e) {
      const raw = e?.response?.data?.detail || e?.message || "Unable to refine right now.";
      setError(typeof raw === "string" ? raw : (raw?.message || "Something went wrong."));
    } finally {
      setRefining(false);
    }
  };

  // Batch-check each affirmation against the user's voice bank.
  const matchVoiceBank = React.useCallback(async (planData) => {
    if (!planData?.sections) return;
    const texts = [];
    for (const sec of planData.sections) {
      for (const ln of sec.lines || []) if (ln.text) texts.push(ln.text);
    }
    if (!texts.length) return setVoiceMatch({});
    try {
      const r = await axios.post(
        `${API}/api/user-voice-bank/match`,
        { texts },
        auth(),
      );
      setVoiceMatch(r.data?.results || {});
    } catch {
      setVoiceMatch({});
    }
  }, []);

  const handleCreate = async () => {
    if (!plan) return;
    // Sequence fix (Feb 22 2026 · Michael's critique): if the user
    // chose to add their own voice AND some lines aren't yet in
    // their bank, route them through the record step BEFORE we
    // compose the audio. This is what makes "Your installation
    // is ready" mean something — nothing is woven until the voice
    // it will carry is actually captured.
    if (voiceChoice === "mine") {
      const newLines = [];
      for (const sec of plan.sections || []) {
        for (const ln of sec.lines || []) {
          if (ln.text && !voiceMatch?.[ln.text]?.recorded) {
            newLines.push({ text: ln.text, section: sec.section });
          }
        }
      }
      if (newLines.length > 0) {
        setPreCreateLines(newLines);
        setStep("preRecord");
        return;
      }
    }
    return doCompose();
  };

  const doCompose = async () => {
    if (!plan) return;
    setStep("creating");
    setError(null);

    // Build one or three payloads, depending on the build-up toggle.
    // Foundation = Truth + Nothing Missing (6 min, gentle).
    // Trust      = Trust + Identity        (8 min, grounded).
    // Playing Free = full journey.
    const seriesGroupId = `sg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const buildPayload = (name, sectionKeys, dur, badge, seriesInfo = null) => {
      const script = {};
      sectionKeys.forEach((k) => {
        if (plan.approved_script[k]) script[k] = plan.approved_script[k];
      });
      const payload = {
        name,
        music_ids: plan.music_ids,
        binaural_stages: plan.binaural_stages,
        affirmation_theme: plan.theme_id,
        duration_minutes: dur,
        custom_concern: `${badge} · ${plan.custom_concern}`.slice(0, 600),
        approved_script: script,
        intention_primer_hz: primerHz,
      };
      // Reclaim-voice gender pick (Feb 24 2026). Only pass the id
      // when the user explicitly chose the male voice — female
      // routes through the backend default (Lily).
      if (voiceChoice === "reclaim" && reclaimVoiceGender === "male") {
        payload.narrator_voice_id = RECLAIM_VOICE_MALE_ID;
      }
      if (seriesInfo) {
        payload.series_group_id = seriesGroupId;
        payload.series_day = seriesInfo.day;
        payload.series_size = 3;
        payload.series_label = seriesInfo.label;
      }
      return payload;
    };

    const wantsBuildUp = addBuildUp && plan.series_suggestion;
    const payloads = wantsBuildUp
      ? [
          buildPayload(`${plan.journey_name} · Foundation`,
                       ["truth", "nothing_missing"], 6, "Day 1 of 3",
                       { day: 1, label: "Foundation" }),
          buildPayload(`${plan.journey_name} · Trust`,
                       ["trust", "identity"], 8, "Day 2 of 3",
                       { day: 2, label: "Trust" }),
          buildPayload(`${plan.journey_name} · Playing Free`,
                       ["truth", "nothing_missing", "trust", "identity",
                        "performance", "final_loop"],
                       plan.duration_minutes, "Day 3 of 3",
                       { day: 3, label: "Playing Free" }),
        ]
      : [
          buildPayload(plan.journey_name,
                       ["truth", "nothing_missing", "trust", "identity",
                        "performance", "final_loop"],
                       plan.duration_minutes, ""),
        ];

    try {
      let last = null;
      // Small retry per journey — a hot-reload or transient ingress
      // hiccup during a series create shouldn't dead-end the user.
      const postWithRetry = async (body) => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const r = await axios.post(`${API}/api/audio-journeys`, body, auth());
            return r.data;
          } catch (err) {
            const status = err?.response?.status;
            const retriable = !status || status === 404 || status === 502 || status === 503 || status === 504;
            if (retriable && attempt < 2) {
              await new Promise((res) => setTimeout(res, 900 + attempt * 900));
              continue;
            }
            throw err;
          }
        }
        return null;
      };
      for (const body of payloads) {
        last = await postWithRetry(body);
      }
      setCreatedJourney(last);
      // Beautiful "curating" moment: stay in the modal, poll the
      // journey until audio_status flips to ready, then show the
      // completion halo before the record step / close.
      if (last?.id) {
        const startedAt = Date.now();
        const POLL_INTERVAL_MS = 2200;
        const MAX_WAIT_MS = 180000; // 3 min hard cap
        while (Date.now() - startedAt < MAX_WAIT_MS) {
          await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
          try {
            const r = await axios.get(`${API}/api/audio-journeys/${last.id}`, auth());
            const s = r.data?.audio_status;
            last = { ...last, ...r.data };
            setCreatedJourney(last);
            if (s === "ready") break;
            // Long wait in the queue: don't hold them on a fake progress bar.
            if (r.data?.queue_position >= 1 && (r.data?.queue_eta_seconds || 0) > 150) break;
            if (s === "failed") { setError("Something didn't compose cleanly. Your library still has this installation — please try playing it in a moment."); break; }
          } catch { /* transient — keep polling */ }
        }
      }
      // Voice recording is now captured BEFORE composition via
      // `PreRecordStep`. Once the audio is woven, we always land on
      // the completion screen — the old post-compose RecordStep
      // (kept for API compat) is no longer part of the flow.
      setStep("landed");
    } catch (e) {
      // Surface a friendly, specific message on the review step so
      // the user isn't left staring at a spinner. Distinguish
      // permanent 4xx from transient 5xx / network.
      const status = e?.response?.status;
      const raw = e?.response?.data?.detail;
      let msg = "Could not create the installation. Please try again.";
      if (typeof raw === "string") msg = raw;
      else if (raw && typeof raw === "object" && raw.message) msg = raw.message;
      if (status === 400 && typeof raw === "string" && raw.startsWith("unknown_music:")) {
        msg = "One of the recommended music tracks isn't available right now. Please try 'Build Your Own' or contact support.";
      } else if (!status) {
        msg = "Lost connection while composing. Please check your network and try again.";
      }
      setError(msg);
      setStep("review");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      style={{
        background: "rgba(6,10,9,0.86)",
        backdropFilter: "blur(18px)",
        // Sit above the app's fixed bottom nav (z-50) and give the
        // scrollable panel breathing room past the iOS home indicator
        // and the nav bar so the primary CTA never hides.
        paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
      }}
      data-testid="askai-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
        className="w-full max-w-2xl rounded-3xl relative"
        style={{
          background: TOK.panelBg,
          border: `1px solid ${TOK.goldMist}`,
          boxShadow: "0 30px 90px rgba(0,0,0,0.55)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 sm:px-8 py-5"
          style={{ borderBottom: `1px solid ${TOK.goldMist}` }}
        >
          <div className="flex items-center gap-3">
            <InstallOrb
              size={38}
              intensity={0.9}
              state={
                step === "thinking"
                  ? "interpreting"
                  : step === "creating"
                  ? "building"
                  : step === "record" || step === "preRecord"
                  ? "listening"
                  : step === "landed"
                  ? "complete"
                  : "resting"
              }
            />
            <div>
              <div
                className="text-[0.68rem] uppercase tracking-[0.28em]"
                style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
              >
                Create with Soulumo
              </div>
              <div
                className="text-lg leading-tight"
                style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
              >
                {(() => {
                  // Header evolves with the journey (Feb 22 2026 · Michael's brief).
                  if (step === "intent")    return "Explain";
                  if (step === "thinking")  return "Soulumo Coach is listening…";
                  if (step === "review")    return "Agree";
                  if (step === "preRecord") return "Record";
                  if (step === "creating")  return "Create";
                  if (step === "landed")    return "Your installation is ready";
                  if (step === "record")    return "Record";
                  return "Explain";
                })()}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:opacity-80"
            style={{ background: "transparent", border: `1px solid ${TOK.goldMist}` }}
            data-testid="askai-close-btn"
          >
            <X size={16} style={{ color: TOK.cream }} />
          </button>
        </div>

        {onBack && (
          <div className="px-6 sm:px-8 pt-4">
            <button type="button" onClick={onBack} data-testid="askai-back-to-simple"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 transition-[background-color,transform] duration-200 active:scale-95"
              style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, background: "rgba(215,187,122,0.08)",
                fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>
              <ChevronLeft size={14} /> Back to simple
            </button>
          </div>
        )}
        <div className="px-6 sm:px-8 py-6">
          <AnimatePresence mode="wait">
            {step === "intent" && (
              <IntentStep
                intent={intent} setIntent={setIntent}
                duration={duration} setDuration={setDuration}
                onSubmit={handleAsk}
                canSubmit={canSubmit}
                error={error}
              />
            )}
            {step === "thinking" && <ThinkingStep />}
            {step === "review" && plan && (
              <ReviewStep
                plan={plan}
                setPlan={setPlan}
                onCreate={handleCreate}
                onBack={() => setStep("intent")}
                error={error}
                addBuildUp={addBuildUp}
                setAddBuildUp={setAddBuildUp}
                primerHz={primerHz}
                setPrimerHz={setPrimerHz}
                voiceChoice={voiceChoice}
                setVoiceChoice={setVoiceChoice}
                reclaimVoiceGender={reclaimVoiceGender}
                setReclaimVoiceGender={setReclaimVoiceGender}
                interpConfirmed={interpConfirmed}
                setInterpConfirmed={setInterpConfirmed}
                refineText={refineText}
                setRefineText={setRefineText}
                onRefine={handleRefine}
                refining={refining}
                voiceMatch={voiceMatch}
                setVoiceMatch={setVoiceMatch}
              />
            )}
            {step === "preRecord" && (
              <PreRecordStep
                lines={preCreateLines}
                voiceMatch={voiceMatch}
                setVoiceMatch={setVoiceMatch}
                onContinue={() => doCompose()}
                onSkipToReclaim={() => { setVoiceChoice("reclaim"); doCompose(); }}
                onBack={() => setStep("review")}
              />
            )}
            {step === "creating" && <CreatingStep name={plan?.journey_name} isSeries={addBuildUp && plan?.series_suggestion} />}
            {step === "landed" && (
              <LandedStep
                name={createdJourney?.name || plan?.journey_name}
                onContinue={() => onCreated && onCreated(createdJourney)}
                usedMyVoice={voiceChoice === "mine" && Object.values(voiceMatch || {}).some((m) => m?.recorded)}
                stillWeaving={createdJourney?.audio_status === "rendering"}
                queueText={createdJourney?.audio_status === "rendering" ? queueLabel(createdJourney) : null}
              />
            )}
            {step === "record" && (
              <RecordStep
                lines={personalisedLines}
                onDone={() => setStep("landed")}
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

function IntentStep({ intent, setIntent, duration, setDuration, onSubmit, canSubmit, error }) {
  return (
    <motion.div
      key="intent"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.28 }}
    >
      <p
        className="italic mb-4"
        style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.05rem" }}
      >
        Tell Soulumo what you&apos;d like to change. You don&apos;t need to know
        what you need — Soulumo Coach will create it with you.
      </p>

      <textarea
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        rows={5}
        placeholder="e.g. I've got a golf competition on Saturday…"
        maxLength={1200}
        data-testid="askai-intent-input"
        className="w-full px-4 py-4 rounded-2xl resize-none focus:outline-none"
        style={{
          background: "rgba(245,236,217,0.04)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.08rem",
          lineHeight: 1.55,
        }}
      />

      <div className="flex items-center justify-between mt-2 text-xs" style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
        <span>{intent.length} / 1200</span>
        <span>Minimum 8 characters</span>
      </div>

      {/* Duration selector */}
      <div className="mt-6">
        <div
          className="text-[0.68rem] uppercase tracking-[0.28em] mb-3"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Duration
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[8, 12, 15, 20, 30].map((m) => (
            <button
              key={m}
              onClick={() => setDuration(m)}
              data-testid={`askai-duration-${m}`}
              className="px-4 py-2 rounded-full transition-all text-xs uppercase tracking-[0.22em]"
              style={{
                background: duration === m ? TOK.goldGlow : "transparent",
                border: `1px solid ${duration === m ? TOK.gold : TOK.goldMist}`,
                color: duration === m ? TOK.cream : TOK.creamDim,
                fontFamily: '"Manrope", sans-serif',
              }}
            >
              {m} min
            </button>
          ))}
        </div>
      </div>

      {/* Examples */}
      <div className="mt-6">
        <div
          className="text-[0.68rem] uppercase tracking-[0.28em] mb-2"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Not sure where to start?
        </div>
        <div className="space-y-2">
          {EXAMPLE_INTENTS.map((ex, i) => (
            <button
              key={i}
              onClick={() => setIntent(ex)}
              data-testid={`askai-example-${i}`}
              className="w-full text-left px-3 py-2 rounded-xl italic text-sm transition-all hover:opacity-90"
              style={{
                background: "rgba(245,236,217,0.03)",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.creamDim,
                fontFamily: '"Cormorant Garamond", serif',
              }}
            >
              "{ex}"
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          className="mt-4 px-4 py-3 rounded-xl text-sm italic"
          style={{
            background: "rgba(216,80,80,0.10)",
            border: "1px solid rgba(216,80,80,0.40)",
            color: "#f0b4b4",
          }}
        >
          {error}
        </div>
      )}

      <div className="mt-8 flex items-center justify-end">
        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          data-testid="askai-submit-intent-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all"
          style={{
            background: canSubmit ? TOK.gold : "rgba(215,187,122,0.25)",
            color: canSubmit ? TOK.ink : TOK.creamMist,
            cursor: canSubmit ? "pointer" : "not-allowed",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <Sparkles size={14} /> Interpret & Build
        </button>
      </div>
    </motion.div>
  );
}

function ThinkingStep() {
  return (
    <motion.div
      key="thinking"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="py-12 text-center"
    >
      <div className="inline-flex flex-col items-center gap-4">
        <InstallOrb size={128} state="interpreting" />
        <div
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.35rem" }}
        >
          Finding the words that already belong to this moment…
        </div>
        <div
          className="italic max-w-sm"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "0.98rem" }}
        >
          Drawing from Reclaim&apos;s Installation Library and shaping only what is uniquely needed for you.
        </div>
      </div>
    </motion.div>
  );
}

function ReviewStep({
  plan, setPlan, onCreate, onBack, error,
  addBuildUp, setAddBuildUp,
  primerHz, setPrimerHz,
  voiceChoice, setVoiceChoice,
  reclaimVoiceGender, setReclaimVoiceGender,
  interpConfirmed, setInterpConfirmed,
  refineText, setRefineText, onRefine, refining,
  voiceMatch, setVoiceMatch,
}) {
  const stats = plan.stats || {};
  const cost = plan.cost || {};
  const series = plan.series_suggestion;
  const [showRefineBox, setShowRefineBox] = React.useState(false);
  // Whether the user has ANY voice-bank entries at all — informs
  // the voice-choice label copy ("Use my recorded voice" vs
  // "Begin my voice library"). Fetched once per open.
  const [bankSize, setBankSize] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/user-voice-bank`, auth());
        if (!cancelled) setBankSize((r.data?.items || []).length);
      } catch { if (!cancelled) setBankSize(0); }
    })();
    return () => { cancelled = true; };
  }, []);
  // Reset the refine box whenever a fresh plan arrives.
  React.useEffect(() => { setShowRefineBox(false); }, [plan?.id]);
  // In-modal privacy sheet — never navigates, so the wizard state
  // is preserved. iter265 scar: `window.open("/voice-library/privacy")`
  // opened same-tab on iOS Safari and destroyed the review.
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  // The orb "lands" with a completion halo the first moment we see
  // the plan, then eases back into rest.
  const [orbState, setOrbState] = React.useState("complete");
  React.useEffect(() => {
    const t = setTimeout(() => setOrbState("resting"), 1800);
    return () => clearTimeout(t);
  }, []);

  // Compute voice coverage for the honest "X in your voice · Y new" chip.
  const totalLines = React.useMemo(() => {
    return (plan.sections || []).reduce((n, s) => n + (s.lines?.length || 0), 0);
  }, [plan]);
  const alreadyRecorded = React.useMemo(() => {
    let n = 0;
    for (const sec of plan.sections || []) {
      for (const ln of sec.lines || []) {
        if (voiceMatch?.[ln.text]?.recorded) n += 1;
      }
    }
    return n;
  }, [plan, voiceMatch]);
  const newToRecord = Math.max(0, totalLines - alreadyRecorded);

  // ── Edit / Delete / Add controls (Feb 24 2026 · Michael's ask) ──
  // Users can now tailor the AI-suggested affirmations before the
  // installation is created — same freedom as the Build Your Own
  // wizard. Every edit stays on this user's private journey; nothing
  // is written back to the shared canonical library.
  const [editingKey, setEditingKey] = React.useState(null); // "section:idx"
  const [editText, setEditText] = React.useState("");
  const [addingSection, setAddingSection] = React.useState(null);
  const [addText, setAddText] = React.useState("");

  // Keep plan.sections (visual) and plan.approved_script (payload) in
  // lock-step so the create call carries whatever the user sees.
  const _syncApprovedScriptFromSections = (nextSections) => {
    const script = { ...(plan.approved_script || {}) };
    for (const sec of nextSections) {
      script[sec.section] = (sec.lines || []).map((ln) => ({ text: ln.text }));
    }
    return script;
  };

  const _mutatePlan = (nextSections, statsDelta = {}) => {
    const approved_script = _syncApprovedScriptFromSections(nextSections);
    const nextStats = { ...(plan.stats || {}) };
    if (statsDelta.canonical) nextStats.canonical_count = Math.max(0, (nextStats.canonical_count || 0) + statsDelta.canonical);
    if (statsDelta.personalised) nextStats.personalised_count = Math.max(0, (nextStats.personalised_count || 0) + statsDelta.personalised);
    if (statsDelta.user_custom) nextStats.user_custom_count = Math.max(0, (nextStats.user_custom_count || 0) + statsDelta.user_custom);
    nextStats.total_lines = nextSections.reduce((n, s) => n + (s.lines?.length || 0), 0);
    setPlan({ ...plan, sections: nextSections, approved_script, stats: nextStats });
  };

  const handleStartEdit = (sectionKey, idx, text) => {
    setEditingKey(`${sectionKey}:${idx}`);
    setEditText(text);
    setAddingSection(null);
  };
  const handleCancelEdit = () => { setEditingKey(null); setEditText(""); };
  const handleSaveEdit = (sectionKey, idx) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    const nextSections = (plan.sections || []).map((sec) => {
      if (sec.section !== sectionKey) return sec;
      const lines = [...(sec.lines || [])];
      const prev = lines[idx] || {};
      // Preserve source semantics but mark as user-edited so the UI
      // shows the "your own line" pill.
      lines[idx] = { ...prev, text: trimmed, source: "user_custom", user_edited: true };
      return { ...sec, lines };
    });
    _mutatePlan(nextSections);
    handleCancelEdit();
  };
  const handleDeleteLine = (sectionKey, idx) => {
    const sec = (plan.sections || []).find((s) => s.section === sectionKey);
    const dropped = sec?.lines?.[idx];
    const nextSections = (plan.sections || []).map((s) => {
      if (s.section !== sectionKey) return s;
      const lines = [...(s.lines || [])];
      lines.splice(idx, 1);
      return { ...s, lines };
    });
    const delta = { canonical: 0, personalised: 0, user_custom: 0 };
    if (dropped?.source === "canonical") delta.canonical = -1;
    else if (dropped?.source === "personalised") delta.personalised = -1;
    else if (dropped?.source === "user_custom") delta.user_custom = -1;
    _mutatePlan(nextSections, delta);
  };
  const handleStartAdd = (sectionKey) => {
    setAddingSection(sectionKey);
    setAddText("");
    setEditingKey(null);
  };
  const handleCancelAdd = () => { setAddingSection(null); setAddText(""); };
  const handleSaveAdd = (sectionKey) => {
    const trimmed = addText.trim();
    if (!trimmed) return;
    const nextSections = (plan.sections || []).map((sec) => {
      if (sec.section !== sectionKey) return sec;
      const lines = [...(sec.lines || []), { text: trimmed, source: "user_custom", user_added: true }];
      return { ...sec, lines };
    });
    _mutatePlan(nextSections, { user_custom: 1 });
    handleCancelAdd();
  };

  return (
    <motion.div
      key="review"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.32 }}
      data-testid="askai-review"
    >
      {/* Interpretation card — always visible, becomes small once confirmed */}
      <div
        className="p-5 rounded-2xl mb-5"
        style={{ background: TOK.goldGlow, border: `1px solid ${TOK.goldMist}` }}
        data-testid="askai-interpretation-card"
      >
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            What Soulumo has read
          </div>
          {interpConfirmed && (
            <button
              onClick={() => setInterpConfirmed(false)}
              data-testid="askai-interpretation-edit"
              className="text-[0.62rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              Refine
            </button>
          )}
        </div>
        <div className="space-y-2" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.02rem" }}>
          <div>
            <div className="text-[0.62rem] uppercase tracking-[0.24em] mb-0.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>Your situation</div>
            <div>{plan.interpretation?.situation}</div>
          </div>
          <div>
            <div className="text-[0.62rem] uppercase tracking-[0.24em] mb-0.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>The pattern</div>
            <div>{plan.interpretation?.current_pattern}</div>
          </div>
          <div>
            <div className="text-[0.62rem] uppercase tracking-[0.24em] mb-0.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>How you want to feel</div>
            <div>{plan.interpretation?.desired_state}</div>
          </div>
          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${TOK.goldMist}` }}>
            <div className="text-[0.62rem] uppercase tracking-[0.24em] mb-0.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>The shift</div>
            <div style={{ color: TOK.gold, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.12rem" }}>{plan.interpretation?.core_shift}</div>
          </div>
        </div>

        {!interpConfirmed && (
          <>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <button
                onClick={() => { setInterpConfirmed(true); setShowRefineBox(false); }}
                data-testid="askai-interpretation-yes"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all"
                style={{
                  background: TOK.gold, color: TOK.ink,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600,
                }}
              >
                <Check size={13} /> Yes, that&apos;s it
              </button>
              <button
                onClick={() => setShowRefineBox((v) => !v)}
                data-testid="askai-interpretation-refine"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all"
                style={{
                  background: "transparent", border: `1px solid ${TOK.gold}`, color: TOK.cream,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase",
                }}
              >
                Refine this
              </button>
            </div>
            {showRefineBox && (
              <div className="mt-4">
                <textarea
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  rows={3}
                  placeholder="That isn't quite what I mean · I want to feel more powerful · Make it less about work and more about trusting myself"
                  data-testid="askai-refine-input"
                  className="w-full px-4 py-3 rounded-xl bg-transparent"
                  style={{
                    color: TOK.cream, border: `1px solid ${TOK.goldMist}`,
                    fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem",
                    resize: "vertical",
                  }}
                />
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={onRefine}
                    disabled={refining || !refineText.trim()}
                    data-testid="askai-refine-submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full"
                    style={{
                      background: TOK.gold, color: TOK.ink,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600,
                      opacity: refining || !refineText.trim() ? 0.6 : 1,
                    }}
                  >
                    {refining ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {refining ? "Re-interpreting…" : "Refine interpretation"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* All content below is gated by interpretation confirmation. */}
      {!interpConfirmed ? null : (
      <>
      {/* Voice choice card — appears BEFORE the full plan is revealed. */}
      <VoiceChoiceCard
        choice={voiceChoice}
        setChoice={setVoiceChoice}
        alreadyRecorded={alreadyRecorded}
        newToRecord={newToRecord}
        hasAnyRecordings={bankSize == null ? alreadyRecorded > 0 : bankSize > 0}
        reclaimVoiceGender={reclaimVoiceGender}
        setReclaimVoiceGender={setReclaimVoiceGender}
      />

      {/* Title */}
      <div className="flex items-center gap-3 mb-1">
        <InstallOrb size={44} state={orbState} intensity={1} />
        <div>
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>Your Installation</div>
          <h2
            className="text-3xl sm:text-4xl font-light leading-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, letterSpacing: "-0.01em" }}
            data-testid="askai-journey-name"
          >
            {plan.journey_name}
          </h2>
        </div>
      </div>

      {/* Coverage strip — restructured (Feb 2026 brief): separates
          where the WORDS came from vs which VOICE recordings exist,
          so no one wonders whether 23 lines has become 45. */}
      <div className="flex items-center gap-2 mt-3 flex-wrap text-xs" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: "rgba(215,187,122,0.15)", border: `1px solid ${TOK.gold}`, color: TOK.gold }}
          data-testid="askai-total-lines"
        >
          <BookOpen size={11} /> {totalLines} lines in your words
        </span>
        <span style={{ color: TOK.creamMist }}>· {plan.duration_minutes} min</span>
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap text-xs" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: "rgba(245,236,217,0.05)", border: `1px solid ${TOK.goldMist}` }}
          data-testid="askai-source-library"
        >
          {stats.canonical_count} from the Installation Library
        </span>
        {stats.personalised_count > 0 && (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: "rgba(215,187,122,0.10)", border: `1px solid ${TOK.gold}`, color: TOK.gold }}
            data-testid="askai-source-personalised"
          >
            <Star size={11} /> {stats.personalised_count} created especially for you
          </span>
        )}
      </div>
      {voiceChoice === "mine" && (
        <div className="flex items-center gap-2 mt-2 flex-wrap text-xs" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
          {alreadyRecorded > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
              style={{ background: "rgba(215,187,122,0.15)", border: `1px solid ${TOK.gold}`, color: TOK.gold }}
              data-testid="askai-voice-coverage-mine"
            >
              <Mic size={11} /> {alreadyRecorded} recorded in your voice
            </span>
          )}
          {newToRecord > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
              style={{ background: "rgba(245,236,217,0.05)", border: `1px solid ${TOK.goldMist}` }}
              data-testid="askai-voice-coverage-new"
            >
              <Sparkles size={11} /> {newToRecord} ready for your voice
            </span>
          )}
        </div>
      )}

      {/* Privacy reassurance — small, warm, above the sections */}
      <button
        type="button"
        onClick={() => setPrivacyOpen(true)}
        className="mt-6 mb-3 w-full flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl text-left transition-all"
        style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
        data-testid="askai-privacy-note"
      >
        <Lock size={13} style={{ color: TOK.gold, marginTop: 2 }} />
        <div
          className="text-[0.72rem] leading-relaxed flex-1"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.95rem" }}
        >
          <span style={{ color: TOK.gold }}>Private to you.</span>{" "}
          You can edit, remove, or add any line. Anything you write stays on your installation only — never shared with other users or added to the shared library.
          <span className="block mt-1 text-[0.62rem] uppercase tracking-[0.24em]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
            Read the full promise →
          </span>
        </div>
      </button>

      {/* Sections */}
      <div className="mt-2 space-y-4" data-testid="askai-sections">
        {plan.sections.map((sec) => (
          <div key={sec.section} className="p-4 rounded-2xl" style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}>
            <div
              className="text-[0.66rem] uppercase tracking-[0.28em] mb-2.5"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              {SECTION_LABELS[sec.section] || sec.section}
            </div>
            <ul className="space-y-2.5">
              {sec.lines.map((ln, idx) => {
                const isEditing = editingKey === `${sec.section}:${idx}`;
                const isUserCustom = ln.source === "user_custom";
                return (
                <li
                  key={idx}
                  className="flex items-start gap-2"
                  data-testid={`askai-line-${sec.section}-${idx}`}
                >
                  <span
                    className="mt-1.5 shrink-0"
                    style={{
                      width: 6, height: 6, borderRadius: 999,
                      background: (ln.source === "personalised" || isUserCustom) ? TOK.gold : TOK.creamMist,
                      boxShadow: (ln.source === "personalised" || isUserCustom) ? `0 0 0 3px ${TOK.goldGlow}` : "none",
                    }}
                  />
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={2}
                          autoFocus
                          data-testid={`askai-line-edit-input-${sec.section}-${idx}`}
                          className="w-full px-3 py-2 rounded-lg bg-transparent"
                          style={{
                            color: TOK.cream, border: `1px solid ${TOK.gold}`,
                            fontFamily: '"Manrope", sans-serif', fontSize: "1rem", resize: "vertical",
                          }}
                        />
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={handleCancelEdit}
                            data-testid={`askai-line-edit-cancel-${sec.section}-${idx}`}
                            className="text-[0.66rem] uppercase tracking-[0.24em]"
                            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveEdit(sec.section, idx)}
                            disabled={!editText.trim()}
                            data-testid={`askai-line-edit-save-${sec.section}-${idx}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                            style={{
                              background: TOK.gold, color: TOK.ink,
                              fontFamily: '"Manrope", sans-serif',
                              fontSize: "0.66rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600,
                              opacity: editText.trim() ? 1 : 0.5,
                            }}
                          >
                            <Check size={11} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {/* Row 1 — affirmation text, full width so
                            long lines never wrap to one-word-per-line
                            (Michael's Feb 11 2026 ask). */}
                        <div
                          style={{
                            fontFamily: '"Manrope", sans-serif',
                            color: TOK.cream,
                            fontSize: "1.02rem",
                            lineHeight: 1.4,
                            fontWeight: 500,
                          }}
                        >
                          {ln.text}
                        </div>
                        {/* Row 2 — compact action strip. Edit / delete
                            live as small ghost icons; play + record
                            keep their existing weight. Right-aligned
                            so the eye stays on the words. */}
                        <div className="mt-2 flex items-center justify-end gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(sec.section, idx, ln.text)}
                            data-testid={`askai-line-edit-${sec.section}-${idx}`}
                            title="Edit this line"
                            aria-label="Edit this line"
                            className="shrink-0 opacity-60 hover:opacity-100 transition-opacity inline-flex items-center justify-center rounded-full"
                            style={{ color: TOK.gold, width: 28, height: 28, border: `1px solid ${TOK.goldMist}` }}
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteLine(sec.section, idx)}
                            data-testid={`askai-line-delete-${sec.section}-${idx}`}
                            title="Remove this line"
                            aria-label="Remove this line"
                            className="shrink-0 opacity-60 hover:opacity-100 transition-opacity inline-flex items-center justify-center rounded-full"
                            style={{ color: TOK.creamMist, width: 28, height: 28, border: `1px solid ${TOK.goldMist}` }}
                          >
                            <Trash2 size={12} />
                          </button>
                          <ReadAloud text={ln.text} size={12} tint={TOK.gold} testId={`askai-readaloud-${sec.section}-${idx}`} />
                          <LineRecordControl
                            text={ln.text}
                            matched={voiceMatch?.[ln.text]}
                            testId={`askai-linerec-${sec.section}-${idx}`}
                            onRecorded={(entry) =>
                              setVoiceMatch((prev) => ({
                                ...prev,
                                [ln.text]: { recorded: true, id: entry.id, text_hash: entry.text_hash, audio_url: entry.audio_url },
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                    {ln.source === "personalised" && !isEditing && (
                      <div
                        className="text-[0.68rem] italic mt-0.5"
                        style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
                      >
                        ★ Created especially for you
                        {ln.why ? (
                          <span style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
                            {" · Soulumo added this because "}{humanizeWhy(ln.why)}
                          </span>
                        ) : null}
                      </div>
                    )}
                    {isUserCustom && !isEditing && (
                      <div
                        className="text-[0.68rem] italic mt-0.5 inline-flex items-center gap-1"
                        style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
                      >
                        <Lock size={9} /> Your own line · private to you
                      </div>
                    )}
                  </div>
                </li>
              );
              })}
            </ul>

            {/* Add-your-own control */}
            {addingSection === sec.section ? (
              <div className="mt-3 flex flex-col gap-2">
                <textarea
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  rows={2}
                  autoFocus
                  placeholder="Write a line in your own words · first person · present tense · 4–13 words"
                  data-testid={`askai-line-add-input-${sec.section}`}
                  className="w-full px-3 py-2 rounded-lg bg-transparent"
                  style={{
                    color: TOK.cream, border: `1px solid ${TOK.gold}`,
                    fontFamily: '"Manrope", sans-serif', fontSize: "1rem", resize: "vertical",
                  }}
                />
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={handleCancelAdd}
                    data-testid={`askai-line-add-cancel-${sec.section}`}
                    className="text-[0.66rem] uppercase tracking-[0.24em]"
                    style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveAdd(sec.section)}
                    disabled={!addText.trim()}
                    data-testid={`askai-line-add-save-${sec.section}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{
                      background: TOK.gold, color: TOK.ink,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.66rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600,
                      opacity: addText.trim() ? 1 : 0.5,
                    }}
                  >
                    <Plus size={11} /> Add line
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleStartAdd(sec.section)}
                data-testid={`askai-line-add-${sec.section}`}
                className="mt-3 inline-flex items-center gap-1.5 text-[0.68rem] uppercase tracking-[0.24em] opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
              >
                <Plus size={11} /> Add your own line
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Cost transparency (subtle) */}
      {cost && (
        <div
          className="mt-5 text-[0.68rem] italic"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
          data-testid="askai-cost-strip"
        >
          Cached · {cost.estimated_tts_chars_saved_first_render || 0} chars ready to play instantly.
        </div>
      )}

      {/* Signature Music arc — the emotional progression Soulumo
          has chosen for this installation. Named stages, adapted to
          the number of tracks. Primary action keeps the Signature. */}
      <MusicArcCard
        musicIds={plan.music_ids || []}
        musicArc={plan.music_arc || []}
        durationMinutes={plan.duration_minutes}
      />

      {/* Binaural summary — one calm chip with the stages, so the
          user sees the frequencies without a technical setup panel. */}
      <BinauralSummary stages={plan.binaural_stages || []} />

      {/* Opening Tone selector — user chooses; nothing prepended
          by default. A pure tone plays for ~60 s before the words
          begin, tuning the nervous system into a receptive state. */}
      <div
        className="mt-5 p-4 rounded-2xl"
        style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
        data-testid="askai-primer-panel"
      >
        <div
          className="text-[0.66rem] uppercase tracking-[0.28em] mb-2.5"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Opening Tone
        </div>
        <div
          className="mb-3"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamMist, fontSize: "0.98rem", lineHeight: 1.4 }}
        >
          A pure tone at the start — about 60 seconds — before the words begin. Optional.
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { hz: null, label: "None",           sub: "Begin immediately" },
            { hz: 396,  label: "396 Hz",         sub: "Release & liberation" },
            { hz: 432,  label: "432 Hz",         sub: "Softening & spaciousness" },
          ].map((opt) => {
            const active = primerHz === opt.hz;
            return (
              <button
                key={String(opt.hz)}
                type="button"
                onClick={() => setPrimerHz(opt.hz)}
                data-testid={`askai-primer-${opt.hz ?? "none"}`}
                className="flex-1 min-w-[130px] px-3 py-2.5 rounded-xl text-left transition-all"
                style={{
                  background: active ? TOK.goldGlow : "rgba(245,236,217,0.02)",
                  border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
                }}
              >
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    color: active ? TOK.gold : TOK.cream,
                    fontSize: "1.05rem",
                    fontWeight: 500,
                  }}
                >
                  {opt.label}
                </div>
                <div
                  className="text-[0.7rem] italic mt-0.5"
                  style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
                >
                  {opt.sub}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional 3-day build-up — only offered when the user's own
          words hint at an upcoming event 2-14 days away. Zero
          configuration — one tap creates all three journeys. */}
      {series && (
        <button
          type="button"
          onClick={() => setAddBuildUp(!addBuildUp)}
          data-testid="askai-buildup-toggle"
          className="mt-5 w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all"
          style={{
            background: addBuildUp ? TOK.goldGlow : "rgba(245,236,217,0.03)",
            border: `1px solid ${addBuildUp ? TOK.gold : TOK.goldMist}`,
          }}
        >
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: addBuildUp ? TOK.gold : "transparent",
              border: `1px solid ${addBuildUp ? TOK.gold : TOK.goldSoft}`,
            }}
          >
            {addBuildUp && <Check size={12} style={{ color: TOK.ink }} strokeWidth={3} />}
          </div>
          <div className="flex-1">
            <div
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                color: TOK.cream,
                fontSize: "1.02rem",
                lineHeight: 1.35,
              }}
            >
              {series.event_hint.charAt(0).toUpperCase() + series.event_hint.slice(1)} is {series.days_out} day{series.days_out === 1 ? "" : "s"} away — build towards it with a three-day series?
            </div>
            <div
              className="text-[0.72rem] italic mt-1.5 leading-relaxed"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
            >
              <span style={{ color: TOK.gold }}>Foundation · Trust · Playing Free</span> — the
              core words deepen across three carefully shaped installations.
              Each day carries the same anchor lines, with a distinct emotional focus.
            </div>
          </div>
        </button>
      )}

      {error && (
        <div
          className="mt-4 px-4 py-3 rounded-xl text-sm italic"
          style={{
            background: "rgba(216,80,80,0.10)",
            border: "1px solid rgba(216,80,80,0.40)",
            color: "#f0b4b4",
          }}
        >
          {error}
        </div>
      )}

      <div className="mt-7 flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          data-testid="askai-back-btn"
          className="text-xs uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          ← Refine intent
        </button>
        <button
          onClick={onCreate}
          data-testid="askai-create-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all"
          style={{
            background: TOK.gold, color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600,
          }}
        >
          {addBuildUp ? "Create 3-Day Series" : "Create My Installation"} <ChevronRight size={14} />
        </button>
      </div>
      </>
      )}

      {/* ── Inline privacy sheet ─────────────────────────────────
          A calm overlay INSIDE the AskAI modal. Never navigates, so
          the wizard state (recordings, script, voice matches) is
          preserved when the user taps "Read the full promise". */}
      {privacyOpen && (
        <PrivacySheet onClose={() => setPrivacyOpen(false)} />
      )}
    </motion.div>
  );
}

// ── In-modal Privacy Promise sheet ───────────────────────────────
// Mirrors the calming copy on /voice-library/privacy but renders
// inline so tapping "read the full promise" never destroys wizard
// state. Reused by ReviewStep and any future in-flow prompt.
const _PRIVACY_ITEMS = [
  { title: "Everything you write stays on your account",
    body: "Every line of your words, everything you explain to Soulumo, every line you edit or add in your own words — all of it is stored only on your private installation. It is never shared with other users, and it is never added to the library that Soulumo draws from." },
  { title: "Every recording is stored under your identity",
    body: "The recordings of your voice live in secure object storage, tagged to your account and no one else's. They are only ever served back to you, only after your login has been verified." },
  { title: "No other user can see any of it",
    body: "Reading a journey — including its title, its lines, its recorded audio — requires the request to come from the same account that made it. There is no path in the app that lets one user browse another user's installation." },
  { title: "Admin tools only see the shared library — never your words",
    body: "The admin dashboard shows counts, popularity of library themes, and system health. It does not surface individual users' custom prompts or personalised lines." },
  { title: "Delete means delete",
    body: "Every recording has a Remove button in your Voice Library. When you press it, the row is taken out of your library and stops being served." },
  { title: "Your voice is only ever used to build your own installations",
    body: "When Soulumo weaves a track together, it looks in your own voice library first — so any line you have spoken becomes part of your future installations, in your voice. Your voice is never used to build anyone else's audio." },
];

function PrivacySheet({ onClose }) {
  React.useEffect(() => {
    const on = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="askai-privacy-sheet-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md max-h-[86vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-6"
        style={{
          background: TOK.ink,
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
        }}
        data-testid="askai-privacy-sheet"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Lock size={16} style={{ color: TOK.gold }} />
            <div>
              <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                Reclaim · Private
              </div>
              <h3
                className="text-2xl font-light leading-tight"
                style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
              >
                How your words are kept private
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="askai-privacy-sheet-close"
            className="shrink-0 inline-flex items-center justify-center rounded-full"
            style={{
              width: 44, height: 44,
              background: "rgba(245,236,217,0.04)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
            }}
          >
            <X size={18} />
          </button>
        </div>
        <p
          className="italic text-[0.94rem] leading-relaxed mb-5"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
        >
          The words you write here — and the recordings of your voice speaking them — are treated as yours. The whole of them, always.
        </p>
        <div className="space-y-3">
          {_PRIVACY_ITEMS.map((p, i) => (
            <div
              key={i}
              className="rounded-2xl p-4"
              style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
              data-testid={`askai-privacy-item-${i}`}
            >
              <div
                className="text-[0.86rem] leading-snug font-medium mb-1.5"
                style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
              >
                {p.title}
              </div>
              <div
                className="text-[0.78rem] leading-relaxed"
                style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
              >
                {p.body}
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={onClose}
          data-testid="askai-privacy-sheet-done"
          className="mt-5 w-full inline-flex items-center justify-center rounded-full transition-all"
          style={{
            background: TOK.gold,
            color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.66rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 600,
            minHeight: 48,
          }}
        >
          Back to my installation
        </button>
      </div>
    </div>
  );
}

// ── Voice Choice card ─────────────────────────────────────────────
// Appears near the start of the plan review — before the full plan
// is revealed. The user picks whether Soulumo should prefer their
// own recorded voice (default) or the Reclaim voice throughout.
// When "reclaim" is chosen, a sub-picker offers Female (Lily,
// default) or Male (Michael's cloned voice — Feb 24 2026).
function VoiceChoiceCard({ choice, setChoice, alreadyRecorded, newToRecord, hasAnyRecordings, reclaimVoiceGender, setReclaimVoiceGender }) {
  const hasBank = hasAnyRecordings ?? (alreadyRecorded > 0);
  return (
    <div
      className="mb-6 p-4 rounded-2xl"
      style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
      data-testid="askai-voice-choice"
    >
      <div
        className="text-[0.66rem] uppercase tracking-[0.28em] mb-3"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        How would you like to receive your words?
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <button
          onClick={() => setChoice("mine")}
          data-testid="askai-voice-mine"
          className="text-left p-3.5 rounded-xl transition-all"
          style={{
            background: choice === "mine" ? TOK.goldGlow : "rgba(245,236,217,0.02)",
            border: `1px solid ${choice === "mine" ? TOK.gold : TOK.goldMist}`,
          }}
        >
          <div
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              color: choice === "mine" ? TOK.gold : TOK.cream,
              fontSize: "1.05rem",
              fontWeight: 500,
            }}
          >
            Add your own voice · Recommended
          </div>
          <div className="text-[0.72rem] italic mt-1" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
            {hasBank
              ? `Soulumo will use every line already in your voice bank, and you'll add your voice to the ${newToRecord ?? "remaining"} lines it doesn't have yet.`
              : "Record each new line once. Soulumo will reuse it in every future installation."}
          </div>
        </button>
        <button
          onClick={() => setChoice("reclaim")}
          data-testid="askai-voice-reclaim"
          className="text-left p-3.5 rounded-xl transition-all"
          style={{
            background: choice === "reclaim" ? TOK.goldGlow : "rgba(245,236,217,0.02)",
            border: `1px solid ${choice === "reclaim" ? TOK.gold : TOK.goldMist}`,
          }}
        >
          <div
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              color: choice === "reclaim" ? TOK.gold : TOK.cream,
              fontSize: "1.05rem",
              fontWeight: 500,
            }}
          >
            Create it now with the Soulumo voice
          </div>
          <div className="text-[0.72rem] italic mt-1" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
            Receive the full installation now — replace any line with your own voice whenever you&apos;re ready.
          </div>
        </button>
      </div>

      {/* Male/Female sub-picker — visible only when Reclaim voice
          is chosen. Female = Lily (backend default); Male = Michael's
          cloned voice. */}
      {choice === "reclaim" && setReclaimVoiceGender && (
        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${TOK.goldMist}` }} data-testid="askai-reclaim-voice-gender">
          <div
            className="text-[0.62rem] uppercase tracking-[0.28em] mb-2"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            Soulumo voice
          </div>
          <div className="flex gap-2" role="radiogroup" aria-label="Soulumo voice gender">
            <button
              type="button"
              role="radio"
              aria-checked={reclaimVoiceGender === "female"}
              onClick={() => setReclaimVoiceGender("female")}
              data-testid="askai-reclaim-voice-female"
              className="flex-1 px-4 py-2.5 rounded-full text-sm transition-all"
              style={{
                background: reclaimVoiceGender === "female" ? TOK.gold : "transparent",
                color: reclaimVoiceGender === "female" ? TOK.ink : TOK.cream,
                border: `1px solid ${reclaimVoiceGender === "female" ? TOK.gold : TOK.goldMist}`,
                fontFamily: '"Manrope", sans-serif',
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontSize: "0.64rem",
                fontWeight: 600,
                minHeight: 40,
              }}
            >
              Female · Lily
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={reclaimVoiceGender === "male"}
              onClick={() => setReclaimVoiceGender("male")}
              data-testid="askai-reclaim-voice-male"
              className="flex-1 px-4 py-2.5 rounded-full text-sm transition-all"
              style={{
                background: reclaimVoiceGender === "male" ? TOK.gold : "transparent",
                color: reclaimVoiceGender === "male" ? TOK.ink : TOK.cream,
                border: `1px solid ${reclaimVoiceGender === "male" ? TOK.gold : TOK.goldMist}`,
                fontFamily: '"Manrope", sans-serif',
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontSize: "0.64rem",
                fontWeight: 600,
                minHeight: 40,
              }}
            >
              Male · Michael
            </button>
          </div>
          <p
            className="mt-2 text-[0.68rem] italic"
            style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
          >
            {reclaimVoiceGender === "male"
              ? "Michael's warm, grounded delivery — cloned from his own studio takes."
              : "Lily's soft British voice — Soulumo's default narrator."}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Per-line record control ───────────────────────────────────────
// A compact mic beside each affirmation on the review screen. If the
// user has already recorded this exact line, shows a subtle "In My
// Voice" badge with playback. If not, tap-to-record; save advances.
export function LineRecordControl({ text, matched, testId, onRecorded }) {
  const [expanded, setExpanded] = React.useState(false);
  const [state, setState] = React.useState("idle"); // idle | recording | uploading | preview
  const [blob, setBlob] = React.useState(null);
  const [mime, setMime] = React.useState("audio/webm");
  const [error, setError] = React.useState("");
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);
  const audioRef = React.useRef(null);
  const isRecorded = !!matched?.recorded;

  const startRec = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const chosen = pickMime();
      const mr = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || chosen || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b); setMime(type); setState("preview");
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      };
      recRef.current = mr;
      mr.start();
      setState("recording");
    } catch (e) {
      setError(e?.message || "mic_denied");
      setState("idle");
    }
  };

  const stopRec = () => {
    const mr = recRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  };

  const playPreview = () => {
    if (!blob || !audioRef.current) return;
    audioRef.current.src = URL.createObjectURL(blob);
    audioRef.current.play().catch(() => {});
  };

  const playRecorded = () => {
    if (!matched?.audio_url || !audioRef.current) return;
    audioRef.current.src = `${API}${matched.audio_url}`;
    audioRef.current.play().catch(() => {});
  };

  const upload = async () => {
    if (!blob) return;
    setState("uploading");
    try {
      const fd = new FormData();
      fd.append("text", text);
      const ext = (mime || "audio/webm").includes("mp4") ? "mp4"
                : (mime || "audio/webm").includes("mpeg") ? "mp3"
                : "webm";
      fd.append("audio", blob, `take.${ext}`);
      const r = await fetch(`${API}/api/user-voice-bank/record`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json();
      onRecorded?.(j);
      setBlob(null);
      setState("idle");
      setExpanded(false);
    } catch (e) {
      setError(e?.message || "save_failed");
      setState("preview");
    }
  };

  // Compact collapsed state.
  if (!expanded) {
    return (
      <>
        <audio ref={audioRef} preload="none" />
        <button
          onClick={() => (isRecorded ? playRecorded() : setExpanded(true))}
          data-testid={testId}
          title={isRecorded ? "In your voice — tap to play" : "Record this line in your voice"}
          className="shrink-0 inline-flex items-center gap-1 rounded-full transition-all"
          style={{
            padding: "3px 8px",
            background: isRecorded ? "rgba(215,187,122,0.16)" : "transparent",
            border: `1px solid ${isRecorded ? TOK.gold : TOK.goldMist}`,
            color: isRecorded ? TOK.gold : TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          {isRecorded ? <Check size={10} /> : <Mic size={10} />}
          {isRecorded ? "In my voice" : "Record"}
        </button>
      </>
    );
  }

  // Expanded recorder.
  return (
    <div
      className="w-full mt-2 p-2.5 rounded-xl"
      style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}
      data-testid={`${testId}-expanded`}
    >
      <audio ref={audioRef} preload="none" />
      <div className="flex items-center gap-2 flex-wrap">
        {state === "idle" && (
          <button
            onClick={startRec}
            data-testid={`${testId}-start`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{
              background: TOK.gold, color: TOK.ink,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
            }}
          >
            <Mic size={11} /> Record
          </button>
        )}
        {state === "recording" && (
          <button
            onClick={stopRec}
            data-testid={`${testId}-stop`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full animate-pulse"
            style={{
              background: "#d85050", color: "#fff",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
            }}
          >
            <Square size={11} /> Stop
          </button>
        )}
        {state === "preview" && (
          <>
            <button
              onClick={playPreview}
              data-testid={`${testId}-play`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: "transparent", border: `1px solid ${TOK.gold}`, color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase",
              }}
            >
              <Play size={11} /> Play
            </button>
            <button
              onClick={() => { setBlob(null); setState("idle"); }}
              data-testid={`${testId}-retake`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase",
              }}
            >
              Retake
            </button>
            <button
              onClick={upload}
              data-testid={`${testId}-save`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: TOK.gold, color: TOK.ink,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
              }}
            >
              <Check size={11} /> Save
            </button>
          </>
        )}
        {state === "uploading" && (
          <span className="inline-flex items-center gap-1.5 text-[0.62rem]" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.18em", textTransform: "uppercase" }}>
            <Loader2 size={11} className="animate-spin" /> Saving…
          </span>
        )}
        <button
          onClick={() => {
            // Stop-and-discard: if we're mid-record, kill the mic and
            // exit; otherwise just collapse the row.
            if (state === "recording") {
              try {
                const mr = recRef.current;
                if (mr && mr.state !== "inactive") mr.stop();
              } catch { /* ignore */ }
              try {
                streamRef.current?.getTracks()?.forEach((t) => t.stop());
              } catch { /* ignore */ }
              setBlob(null);
              setState("idle");
              setExpanded(false);
              return;
            }
            if (state === "uploading") return;
            setExpanded(false);
            setBlob(null);
            setState("idle");
          }}
          data-testid={`${testId}-cancel`}
          disabled={state === "uploading"}
          className="ml-auto text-[0.6rem]"
          style={{
            color: state === "uploading" ? "rgba(245,224,173,0.25)" : TOK.creamMist,
            fontFamily: '"Manrope", sans-serif', letterSpacing: "0.14em", textTransform: "uppercase",
            opacity: state === "uploading" ? 0.4 : 1,
          }}
        >
          Cancel
        </button>
      </div>
      {error && <div className="text-[0.62rem] italic mt-1" style={{ color: "#dc8b6c" }}>{error}</div>}
    </div>
  );
}

// ── Music Arc card ────────────────────────────────────────────────
// Soulumo's Signature Selection — shows the emotional arc of the
// music chosen for this installation as named stages, adapted to
// track count. Users can keep it (default, no-op) or open the
// manual wizard for full music control.
function MusicArcCard({ musicIds, musicArc, durationMinutes }) {
  const trackCount = Array.isArray(musicIds) ? musicIds.length : 0;
  const hasRichArc = Array.isArray(musicArc) && musicArc.length > 0;

  // ── Preview On Tap (Feb 24 2026) ────────────────────────────────
  // Each stage row gets a tap-to-preview button. Plays a 20s snippet
  // from the resolved ambient track. Only one plays at a time. Auto-
  // stops at 20s. Cleans up on unmount.
  const [previewIdx, setPreviewIdx] = React.useState(null);
  const audioRef = React.useRef(null);
  const stopTimerRef = React.useRef(null);

  const stopPreview = React.useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    setPreviewIdx(null);
  }, []);

  React.useEffect(() => stopPreview, [stopPreview]);

  const togglePreview = (idx) => {
    if (previewIdx === idx) { stopPreview(); return; }
    stopPreview();
    const mid = musicIds?.[idx] || musicIds?.[0];
    if (!mid || !mid.startsWith("ambient:")) return;
    const stem = mid.split(":", 2)[1];
    const url = `${API}/api/audio/ambient/${stem}.mp3`;
    const a = new Audio(url);
    a.volume = 0.7;
    a.play().catch((e) => { console.warn("[askai] preview failed:", e); stopPreview(); });
    audioRef.current = a;
    setPreviewIdx(idx);
    stopTimerRef.current = setTimeout(() => stopPreview(), 20_000);
    a.addEventListener("ended", () => setPreviewIdx(null), { once: true });
  };

  // Fallback stage names if the backend didn't supply the rich arc.
  const FALLBACK_ARCS = {
    1: [{ name: "Presence", intent: "one steady soundscape holding the whole installation" }],
    2: [
      { name: "Grounding",  intent: "bringing the nervous system into safety" },
      { name: "Integration", intent: "allowing the installation to land" },
    ],
    3: [
      { name: "Grounding",  intent: "bringing the nervous system into safety" },
      { name: "Rising",     intent: "building trust, confidence and possibility" },
      { name: "Integration", intent: "allowing the installation to land" },
    ],
    4: [
      { name: "Grounding",  intent: "bringing the nervous system into safety" },
      { name: "Opening",    intent: "becoming receptive to the words" },
      { name: "Rising",     intent: "building trust, confidence and possibility" },
      { name: "Integration", intent: "allowing the installation to land" },
    ],
    5: [
      { name: "Grounding",  intent: "bringing the nervous system into safety" },
      { name: "Opening",    intent: "becoming receptive to the words" },
      { name: "Rising",     intent: "building trust, confidence and possibility" },
      { name: "Embodiment", intent: "settling into the new identity" },
      { name: "Integration", intent: "allowing the installation to land" },
    ],
  };
  const arc = hasRichArc
    ? musicArc.map((s) => ({ name: s.name, intent: s.intent }))
    : (FALLBACK_ARCS[Math.max(1, Math.min(5, trackCount || 3))] || FALLBACK_ARCS[3]);

  return (
    <div
      className="mt-5 p-4 rounded-2xl"
      style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
      data-testid="askai-music-arc"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div
          className="text-[0.66rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Your Music
        </div>
        <div className="text-[0.62rem]" style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.14em", textTransform: "uppercase" }}>
          {trackCount || "—"} track{trackCount === 1 ? "" : "s"} · {durationMinutes} min
        </div>
      </div>
      <div
        className="mb-3"
        style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.08rem" }}
      >
        Soulumo Signature Selection <span style={{ color: TOK.gold }}>— Recommended</span>
      </div>
      <div className="text-[0.72rem] italic mb-3" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
        Selected and arranged for the emotional arc of this installation.
      </div>
      <ol className="space-y-1.5">
        {arc.map((s, i) => {
          const isPreviewing = previewIdx === i;
          const previewable = Array.isArray(musicIds) && !!(musicIds[i] || musicIds[0]);
          return (
          <li key={i} className="flex items-baseline gap-2" data-testid={`askai-arc-stage-${i}`}>
            <span
              className="w-4 shrink-0 text-[0.62rem] tabular-nums"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              {i + 1}.
            </span>
            {previewable && (
              <button
                onClick={() => togglePreview(i)}
                data-testid={`askai-arc-preview-${i}`}
                aria-label={isPreviewing ? "Stop preview" : "Play 20-second preview"}
                className="shrink-0 inline-flex items-center justify-center rounded-full transition-all"
                style={{
                  width: 24, height: 24,
                  background: isPreviewing ? TOK.gold : "rgba(215,187,122,0.10)",
                  border: `1px solid ${isPreviewing ? TOK.gold : TOK.goldMist}`,
                  color: isPreviewing ? TOK.ink : TOK.gold,
                }}
              >
                {isPreviewing ? (
                  <span className="block" style={{ width: 7, height: 7, background: TOK.ink }} />
                ) : (
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3l14 9-14 9V3z" /></svg>
                )}
              </button>
            )}
            <div className="flex-1">
              <span
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  color: TOK.cream,
                  fontSize: "1.04rem",
                }}
              >
                {s.name}
              </span>
              <span className="text-[0.72rem] italic ml-2" style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
                — {s.intent}
              </span>
              {isPreviewing && (
                <span className="text-[0.6rem] uppercase tracking-[0.2em] italic ml-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                  previewing…
                </span>
              )}
            </div>
          </li>
          );
        })}
      </ol>
      {/* Explicit Signature confirmation. "Choose my own music"
          is hidden until it works properly — a disabled control
          interrupts the feeling that this is a complete journey. */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="askai-music-actions">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{
            background: TOK.goldGlow,
            border: `1px solid ${TOK.gold}`,
            color: TOK.gold,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
          }}
          data-testid="askai-music-keep-signature"
        >
          <Check size={11} /> Keep Soulumo&apos;s Signature Selection — Recommended
        </div>
      </div>
    </div>
  );
}

// ── Binaural summary chip ─────────────────────────────────────────
// A calm, non-technical chip listing the binaural stages Soulumo
// has matched to this installation.
function BinauralSummary({ stages }) {
  if (!stages || !stages.length) return null;
  // Stages arrive as [{start_seconds, end_seconds, band}] — we
  // dedupe by band and show the emotional band labels.
  const BAND_LABEL = {
    delta: "Delta · deep integration",
    theta: "Theta · receptive stillness",
    alpha: "Alpha · calm attention",
    beta:  "Beta · engaged focus",
    gamma: "Gamma · sharp presence",
  };
  const bands = [];
  const seen = new Set();
  for (const s of stages) {
    const b = (s.band || "").toLowerCase();
    if (!b || seen.has(b)) continue;
    seen.add(b);
    bands.push({ id: b, label: BAND_LABEL[b] || b });
  }
  if (!bands.length) return null;
  return (
    <div
      className="mt-3 flex items-center gap-2 flex-wrap"
      data-testid="askai-binaural-summary"
    >
      <span
        className="text-[0.62rem] uppercase tracking-[0.28em]"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        Binaural
      </span>
      {bands.map((b) => (
        <span
          key={b.id}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
          style={{
            background: "rgba(245,236,217,0.04)",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.cream,
            fontFamily: '"Manrope", sans-serif',
          }}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

function CreatingStep({ name, isSeries }) {
  // Progress-driven creation step (see stages below).
  const [seconds, setSeconds] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Six named creation stages. We march through them on a soft
  // cadence and drive both the orb state and progress ring from
  // the current index, so the wait feels genuinely alive.
  const STAGES = React.useMemo(() => (isSeries ? [
    { label: "Understanding your situation",   orb: "interpreting" },
    { label: "Shaping your Foundation lines",  orb: "curating"     },
    { label: "Weaving your Trust installation", orb: "curating"    },
    { label: "Composing your full installation", orb: "installing" },
    { label: "Bringing everything together",   orb: "installing"   },
    { label: "Almost ready — one soft breath", orb: "installing"   },
  ] : [
    { label: "Understanding your situation",   orb: "interpreting" },
    { label: "Arranging your words",   orb: "interpreting" },
    { label: "Selecting your music",           orb: "curating"     },
    { label: "Matching your frequencies",      orb: "curating"     },
    { label: "Bringing in your voice",         orb: "installing"   },
    { label: "Weaving your installation",      orb: "installing"   },
  ]), [isSeries]);
  const STAGE_SECS = 8; // seconds per stage
  const stageIdx = Math.min(STAGES.length - 1, Math.floor(seconds / STAGE_SECS));
  const stage = STAGES[stageIdx];
  // Progress: fills as stages complete + partial fill within current stage.
  const progressPct = Math.min(
    98,
    Math.round(((stageIdx + ((seconds % STAGE_SECS) / STAGE_SECS)) / STAGES.length) * 100),
  );

  // Reassure the user if generation is running long.
  const longWait = seconds > 45;

  const ringSize = 260;
  const stroke = 2;
  const r = (ringSize - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - progressPct / 100);

  return (
    <motion.div
      key="creating"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="py-6 text-center"
      data-testid="askai-creating-step"
    >
      <div className="inline-flex flex-col items-center gap-5 w-full">
        {/* Orb with a real progress ring around it */}
        <div className="relative" style={{ width: ringSize, height: ringSize }}>
          <svg width={ringSize} height={ringSize} className="absolute inset-0" style={{ transform: "rotate(-90deg)" }} aria-hidden>
            <circle cx={ringSize / 2} cy={ringSize / 2} r={r}
              stroke="rgba(231,201,138,0.14)" strokeWidth={stroke} fill="none" />
            <circle cx={ringSize / 2} cy={ringSize / 2} r={r}
              stroke="rgba(231,201,138,0.85)" strokeWidth={stroke} fill="none"
              strokeDasharray={c} strokeDashoffset={dashOffset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 900ms ease-out" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <InstallOrb size={220} state={stage.orb} />
          </div>
        </div>

        <div
          className="text-[0.68rem] uppercase tracking-[0.32em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Installing · {progressPct}%
        </div>

        <div
          className="max-w-md"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.35rem", lineHeight: 1.3 }}
        >
          {name || "Your installation"}
        </div>

        <motion.div
          key={stage.label}
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="italic max-w-sm"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.02rem" }}
          data-testid="askai-creating-stage"
        >
          {stage.label}…
        </motion.div>

        {longWait && (
          <div
            className="text-[0.72rem] italic max-w-xs mt-1"
            style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
            data-testid="askai-creating-longwait"
          >
            Your installation is still being carefully woven. You can stay
            here — we&apos;ll let you know the moment it&apos;s ready.
          </div>
        )}

        <div
          className="text-[0.62rem] tabular-nums"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.24em" }}
        >
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </div>
      </div>
    </motion.div>
  );
}

function LandedStep({ name, onContinue, usedMyVoice, stillWeaving, queueText }) {
  // The moment of arrival — orb "lands" with a completion halo,
  // then settles into rest.
  const [orbState, setOrbState] = React.useState("complete");
  React.useEffect(() => {
    const t = setTimeout(() => setOrbState("resting"), 1800);
    return () => clearTimeout(t);
  }, []);
  return (
    <motion.div
      key="landed"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="py-6 text-center"
      data-testid="askai-landed-step"
    >
      <div className="inline-flex flex-col items-center gap-5 w-full">
        <InstallOrb size={220} state={stillWeaving ? "installing" : orbState} />
        <div
          className="text-[0.68rem] uppercase tracking-[0.32em]"
          style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
          data-testid="askai-landed-status"
        >
          {stillWeaving ? "Still being woven" : "Your installation is ready"}
        </div>
        {stillWeaving && queueText && (
          <div className="text-sm italic" style={{ color: TOK.gold, fontFamily: '"Cormorant Garamond", serif' }} data-testid="askai-landed-queue">
            {queueText}
          </div>
        )}
        <div
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.85rem", lineHeight: 1.15 }}
        >
          {name}
        </div>
        <p
          className="max-w-sm"
          style={{ fontFamily: '"Manrope", sans-serif', color: TOK.creamDim, fontSize: "0.94rem", lineHeight: 1.55 }}
        >
          {stillWeaving
            ? "Everything is saved. The final weave is still finishing in the background \u2014 it will appear in your library with a play button the moment it\u2019s ready. You can safely leave this screen."
            : usedMyVoice
            ? "Your words, your voice, Reclaim\u2019s music and the ideal binaural rhythm have now been woven into one complete installation. Play it when you are ready."
            : "Your words, Reclaim\u2019s voice, music and the ideal binaural rhythm have now been woven into one complete installation. Play it when you are ready."}
        </p>
        <button
          onClick={onContinue}
          data-testid="askai-landed-continue-btn"
          className="mt-2 inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all"
          style={{
            background: TOK.gold, color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600,
          }}
        >
          {stillWeaving ? "Go to my library" : "Begin my installation"} <ChevronRight size={14} />
        </button>
      </div>
    </motion.div>
  );
}

// ── Voice Bank recording step ────────────────────────────────────
//
// After a journey is created, if Claude proposed 1-4 personalised
// lines, we offer the user a calm 30-second-per-line recording flow.
// Each recording is stored in `user_voice_bank` keyed by text_hash
// — so the NEXT journey that uses any of these lines is £0 for TTS.
//
// Simple UX (Michael's rule):
//   · One card per line. Tap mic → record → tap stop → auto-save.
//   · Everything else is a "Skip for now" button.
//   · No trim, no effects, no re-do carousel. Save · Delete · Move on.

function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const cands = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const c of cands) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* older browsers throw */
    }
  }
  return "";
}

function LineRecorder({ line, onSaved }) {
  const [state, setState] = React.useState("idle"); // idle | recording | uploading | saved | error
  const [elapsed, setElapsed] = React.useState(0);
  const [errMsg, setErrMsg] = React.useState("");
  // Web Audio analyser — drives the InstallOrb's recording pulse.
  const [analyser, setAnalyser] = React.useState(null);
  const audioCtxRef = React.useRef(null);
  const recRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timerRef = React.useRef(null);
  const MAX_SECONDS = 30;

  const stop = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      recRef.current?.stop();
    } catch {
      /* already stopped */
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    setAnalyser(null);
  }, []);

  const start = async () => {
    setErrMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Set up the analyser to drive the orb pulse.
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (Ctor) {
          const ctx = new Ctor();
          audioCtxRef.current = ctx;
          const src = ctx.createMediaStreamSource(stream);
          const an = ctx.createAnalyser();
          an.fftSize = 256;
          src.connect(an);
          setAnalyser(an);
        }
      } catch {
        // Analyser is optional; recording still works without it.
      }
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 512) {
          setErrMsg("That felt too brief — try holding the phrase for a full breath.");
          setState("idle");
          return;
        }
        setState("uploading");
        try {
          const form = new FormData();
          form.append("text", line.text);
          form.append("audio", blob, "line.webm");
          const r = await axios.post(`${API}/api/user-voice-bank/record`, form, {
            headers: {
              Authorization: `Bearer ${getToken()}`,
              "Content-Type": "multipart/form-data",
            },
          });
          setState("saved");
          onSaved && onSaved(r.data);
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || "upload_failed";
          setErrMsg(typeof detail === "string" ? detail : "Something went wrong.");
          setState("error");
        }
      };
      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((v) => {
          const next = v + 1;
          if (next >= MAX_SECONDS) stop();
          return next;
        });
      }, 1000);
    } catch (e) {
      setErrMsg(e?.message || "Microphone permission denied.");
      setState("error");
    }
  };

  React.useEffect(() => () => stop(), [stop]);

  return (
    <div
      className="p-4 rounded-2xl"
      style={{
        background: state === "saved" ? "rgba(215,187,122,0.10)" : "rgba(245,236,217,0.03)",
        border: `1px solid ${state === "saved" ? TOK.gold : TOK.goldMist}`,
      }}
      data-testid={`voicebank-line-${line.section}`}
    >
      <div className="flex items-start gap-2">
        <p
          className="flex-1"
          style={{
            fontFamily: '"Manrope", sans-serif',
            color: TOK.cream,
            fontSize: "1.12rem",
            lineHeight: 1.4,
            fontWeight: 500,
          }}
        >
          {line.text}
        </p>
        <ReadAloud text={line.text} size={13} tint={TOK.gold} testId={`voicebank-readaloud-${line.section}`} />
      </div>
      <div className="mt-3 flex items-center gap-3">
        {state === "idle" && (
          <button
            onClick={start}
            data-testid={`voicebank-record-btn`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: TOK.goldGlow, border: `1px solid ${TOK.gold}`, color: TOK.cream,
              fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
              letterSpacing: "0.22em", textTransform: "uppercase",
            }}
          >
            <Mic size={13} /> Record my voice
          </button>
        )}
        {state === "recording" && (
          <>
            <InstallOrb size={44} state="recording" analyserNode={analyser} />
            <button
              onClick={stop}
              data-testid={`voicebank-stop-btn`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: "rgba(224,122,106,0.16)", border: "1px solid #E07A6A", color: "#E07A6A",
                fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
                letterSpacing: "0.22em", textTransform: "uppercase",
              }}
            >
              <Square size={12} fill="currentColor" /> Stop · {elapsed}s
            </button>
          </>
        )}
        {state === "uploading" && (
          <span
            className="inline-flex items-center gap-2 text-xs"
            style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
          >
            <Loader2 size={13} className="animate-spin" /> Saving…
          </span>
        )}
        {state === "saved" && (
          <span
            className="inline-flex items-center gap-2 text-xs"
            style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
          >
            <Check size={13} /> Saved · this line is now free for future installations
          </span>
        )}
        {state === "error" && (
          <button
            onClick={start}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs"
            style={{ background: TOK.goldGlow, border: `1px solid ${TOK.gold}`, color: TOK.cream }}
          >
            Try again
          </button>
        )}
      </div>
      {errMsg && (
        <div
          className="mt-2 text-xs italic"
          style={{ color: "#f0b4b4", fontFamily: '"Manrope", sans-serif' }}
        >
          {errMsg}
        </div>
      )}
    </div>
  );
}

function PreRecordStep({ lines, voiceMatch, setVoiceMatch, onContinue, onSkipToReclaim, onBack }) {
  const [skipped, setSkipped] = React.useState({});
  const remaining = React.useMemo(
    () => lines.filter((l) => !voiceMatch?.[l.text]?.recorded && !skipped[l.text]),
    [lines, voiceMatch, skipped],
  );
  const recordedCount = lines.filter((l) => voiceMatch?.[l.text]?.recorded).length;
  const canContinue = remaining.length === 0;

  return (
    <motion.div
      key="preRecord"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.32 }}
      data-testid="askai-prerecord"
    >
      <div className="flex items-center gap-3 mb-2">
        <InstallOrb size={40} state="recording" />
        <div>
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Add Your Voice
          </div>
          <h2 className="text-2xl sm:text-3xl font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}>
            The lines Soulumo needs from you
          </h2>
        </div>
      </div>

      <p className="italic text-sm mt-3 mb-5" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
        {lines.length} line{lines.length === 1 ? "" : "s"} created just for you.
        Record each once and it will join your voice bank — whenever a future installation uses the same line, it will already be there in your voice.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="prerecord-progress">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
          style={{ background: "rgba(215,187,122,0.15)", border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
          <Mic size={11} /> {recordedCount === 0 ? `${lines.length} to record` : `${recordedCount} now in your voice`}
        </span>
        {Object.keys(skipped).length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
            style={{ background: "rgba(245,236,217,0.04)", border: `1px solid ${TOK.goldMist}`, color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
            {Object.keys(skipped).length} using Soulumo voice
          </span>
        )}
      </div>

      <ul className="space-y-3" data-testid="prerecord-lines">
        {lines.map((ln, idx) => {
          const isRecorded = !!voiceMatch?.[ln.text]?.recorded;
          const isSkipped = !!skipped[ln.text];
          return (
            <li key={idx} className="p-3 rounded-2xl"
              style={{
                background: isRecorded ? "rgba(215,187,122,0.08)" : isSkipped ? "rgba(245,236,217,0.02)" : "rgba(245,236,217,0.03)",
                border: `1px solid ${isRecorded ? TOK.gold : TOK.goldMist}`,
                opacity: isSkipped ? 0.65 : 1,
              }}
              data-testid={`prerecord-line-${idx}`}>
              <div className="flex items-start gap-2">
                <span className="mt-1.5 shrink-0" style={{ width: 6, height: 6, borderRadius: 999, background: isRecorded ? TOK.gold : TOK.creamMist }} />
                <div className="flex-1">
                  <div style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.12rem", lineHeight: 1.4 }}>
                    {ln.text}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ReadAloud text={ln.text} size={12} tint={TOK.gold} testId={`prerecord-readaloud-${idx}`} />
                    <LineRecordControl
                      text={ln.text}
                      matched={voiceMatch?.[ln.text]}
                      testId={`prerecord-rec-${idx}`}
                      onRecorded={(entry) => setVoiceMatch((prev) => ({
                        ...prev,
                        [ln.text]: { recorded: true, id: entry.id, text_hash: entry.text_hash, audio_url: entry.audio_url },
                      }))}
                    />
                    {!isRecorded && (
                      <button onClick={() => setSkipped((s) => ({ ...s, [ln.text]: !s[ln.text] }))}
                        data-testid={`prerecord-skip-${idx}`}
                        className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.14em] px-2 py-1 rounded-full"
                        style={{ background: isSkipped ? "rgba(245,236,217,0.05)" : "transparent", color: TOK.creamMist, border: `1px solid ${TOK.goldMist}`, fontFamily: '"Manrope", sans-serif' }}>
                        {isSkipped ? "Skipped · undo" : "Use Soulumo voice for this line"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} data-testid="prerecord-back"
          className="text-xs uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
          ← Back to plan
        </button>
        <div className="flex flex-wrap gap-2">
          <button onClick={onSkipToReclaim} data-testid="prerecord-skip-all"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            Create with Soulumo voice
          </button>
          <button onClick={onContinue} disabled={!canContinue} data-testid="prerecord-continue"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
            style={{ background: TOK.gold, color: TOK.ink, fontFamily: '"Manrope", sans-serif', fontSize: "0.78rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600, opacity: canContinue ? 1 : 0.5 }}>
            {canContinue ? "Weave my installation" : `${remaining.length} left`} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}


function RecordStep({ lines, onDone }) {
  const [savedCount, setSavedCount] = React.useState(0);
  return (
    <motion.div
      key="record"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.32 }}
      data-testid="askai-record-step"
    >
      <div
        className="text-[0.68rem] uppercase tracking-[0.28em] mb-2"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        Made just for you
      </div>
      <h2
        className="text-2xl sm:text-3xl font-light mb-2"
        style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, letterSpacing: "-0.01em" }}
      >
        {lines.length === 1
          ? "One line, in your voice"
          : `${lines.length} lines, in your voice`}
      </h2>
      <p
        className="italic mb-5"
        style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.02rem" }}
      >
        Record these once. They join your voice bank, so every future
        installation that uses them plays back in your voice — no waiting,
        no re-generation.
      </p>

      <div className="space-y-3">
        {lines.map((ln, i) => (
          <LineRecorder
            key={`${ln.section}-${i}-${ln.text}`}
            line={ln}
            onSaved={() => setSavedCount((n) => n + 1)}
          />
        ))}
      </div>

      <div className="mt-7 flex items-center justify-between gap-3">
        <button
          onClick={onDone}
          data-testid="askai-skip-recording-btn"
          className="text-xs uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          Skip for now
        </button>
        <button
          onClick={onDone}
          data-testid="askai-recording-done-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all"
          style={{
            background: TOK.gold, color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600,
          }}
        >
          {savedCount === lines.length ? "All done" : "I'm done"} <ChevronRight size={14} />
        </button>
      </div>
    </motion.div>
  );
}

